import { useReducer, useCallback } from 'react';
import { TableState } from 'types';

type Action<TableType extends object, FilterType extends object | undefined> =
  | {
      type: 'Set filter state';
      payload: FilterType;
    }
  | {
      type: 'Set table state';
      payload: Omit<TableState<TableType, FilterType>, 'filters'>;
    };

export function useTableState<TableType extends object, FilterType extends object | undefined>(
  initialState: TableState<TableType, FilterType>
) {
  const [tableLocalState, dispatch] = useReducer<
    (
      state: TableState<TableType, FilterType>,
      action: Action<TableType, FilterType>
    ) => TableState<TableType, FilterType>
  >(tableReducer, initialState);

  const setFilterState = useCallback(
    (filterState: FilterType) => dispatch({ type: 'Set filter state', payload: filterState }),
    []
  );
  const setTableState = useCallback(
    (tableState: Omit<TableState<TableType, FilterType>, 'filters'>) =>
      dispatch({ type: 'Set table state', payload: tableState }),
    []
  );

  return { tableState: tableLocalState, setFilterState, setTableState };
}

function tableReducer<TableType extends object, FilterType extends object | undefined>(
  state: TableType,
  action: Action<TableType, FilterType>
): TableType {
  switch (action.type) {
    case 'Set filter state':
      return { ...state, filters: action.payload, pageIndex: 0 };
    case 'Set table state':
      return {
        ...state,
        pageSize: action.payload.pageSize,
        pageIndex: action.payload.pageIndex,
        sortBy: action.payload.sortBy,
      };
    default:
      throw new Error('This action should never happen');
  }
}
